import React, { Component } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Pagination, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import TimelineIcon from '@mui/icons-material/Timeline';
import LanIcon from '@mui/icons-material/Lan';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CustomizedSnackbars from '../../components/material-snackbars';
import Api from '../../services/api';
import UsageService from '../../services/usage.service';
import UsagePeriodFilter, { rangeFromQuery } from '../../components/usage/usage-period-filter';
import { UsageKpiCard, UsageEmpty, UsageChipFilter, UsageEllipsisCell, UsageGeoAttribution, STICKY_CELL_CLASS, fmtDateTime, fmtInt } from '../../components/usage/usage-widgets';
import { t } from '../../components/usage/usage-i18n';

// ---------------------------------------------------------------------------
// Item 2 — ALERTAS DE SEGURANÇA: o que o BackgroundTasks detectou (país novo, datacenter/Tor, rajada de falhas de login...).
// Só Global. Aqui se reconhece/resolve (com observação) e se pula para a investigação (linha do tempo, tráfego por IP, sessões).
// Evidência = JSON gravado pela regra; a tela mostra os campos conhecidos por extenso.
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;
const SEVERITY_COLORS = { critical: '#c62828', warning: '#ef6c00', info: '#607D8B' };
const STATUS_COLORS = { open: '#c62828', acknowledged: '#1565c0', resolved: '#2e7d32' };
const RULES = ['new_country_login', 'hosting_or_tor_ip', 'login_failures', 'denial_burst', 'export_burst', 'volume_anomaly'];

export default class UsageAlerts extends Component {
  constructor(props) {
    super(props);
    const q = new URLSearchParams(props.location?.search || '');
    this.state = {
      filter: { ...rangeFromQuery(q, '30d'), api: '', actionGroup: '', userId: Number(q.get('userId')) || 0, userName: q.get('userName') || '', ip: props.ip || '' },
      status: q.get('status') || 'open', severity: q.get('severity') || '', rule: q.get('rule') || '',
      loading: false, loaded: false, rows: [], count: 0, page: 1, summary: null,
      dialog: null, // { row, action: 'acknowledged' | 'resolved', note }
      saving: false, snack: { open: false, variant: 'error', message: '' },
    };
    this.onFilter = this.onFilter.bind(this);
    this.load = this.load.bind(this);
    this.onPage = this.onPage.bind(this);
    this.closeSnack = this.closeSnack.bind(this);
    this.confirmStatus = this.confirmStatus.bind(this);
  }

  componentDidMount() { this.lastSearch = this.props.location?.search || ''; this.load(1); }

  componentDidUpdate(prevProps) {
    if ((prevProps.ip || '') !== (this.props.ip || '') && (this.props.ip || '') !== (this.state.filter.ip || '')) {
      this.setState((s) => ({ filter: { ...s.filter, ip: this.props.ip || '' } }), () => { if (this.state.loaded) this.load(1); });
      return;
    }
    const search = this.props.location?.search || '';
    if (this.props.active && search && search !== this.lastSearch) {
      this.lastSearch = search;
      const q = new URLSearchParams(search);
      this.setState((s) => ({ filter: { ...s.filter, userId: Number(q.get('userId')) || 0, userName: q.get('userName') || '' }, status: q.get('status') || s.status }), () => this.load(1));
    }
  }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }
  notify(variant, message) { this.setState({ snack: { open: true, variant, message } }); }
  closeSnack() { this.setState({ snack: { ...this.state.snack, open: false } }); }

  onFilter(next) {
    if (this.props.onIpChange && (next.ip || '') !== (this.state.filter.ip || '')) this.props.onIpChange(next.ip || '');
    this.setState({ filter: next });
  }

  onPage(e, page) { this.load(page); }

  load(page) {
    const { filter, status, severity, rule } = this.state;
    this.setState({ loading: true, page });
    Promise.all([
      UsageService.alerts(filter, { status, severity, rule }, page, PAGE_SIZE),
      UsageService.alertsSummary().catch(() => null),
    ])
      .then(([r, summary]) => this.setState({ loading: false, loaded: true, rows: r.data, count: r.count, summary }))
      .catch((err) => { Api.kickoff(err); this.setState({ loading: false }); this.notify('error', err.message); });
  }

  setChip(patch) { this.setState(patch, () => this.load(1)); }

  openDialog(row, action) { this.setState({ dialog: { row, action, note: '' } }); }

  confirmStatus() {
    const { dialog } = this.state;
    if (!dialog) return;
    this.setState({ saving: true });
    UsageService.alertStatus(dialog.row.id, dialog.action, dialog.note)
      .then(() => {
        this.setState({ saving: false, dialog: null });
        this.notify('success', dialog.action === 'resolved' ? this.tr('usage.alerts.resolved', 'Alerta resolvido.') : this.tr('usage.alerts.acknowledged', 'Alerta reconhecido.'));
        this.load(this.state.page);
      })
      .catch((err) => { this.setState({ saving: false }); this.notify('error', err.message); });
  }

  // "Investigar": abre a aba certa já filtrada pelo alvo e pelo dia do alerta (± 1 dia).
  investigate(row, tab) {
    const q = new URLSearchParams();
    const day = moment(row.firstAt);
    q.set('start', day.clone().subtract(1, 'day').format('YYYY-MM-DD')); q.set('end', day.clone().add(1, 'day').format('YYYY-MM-DD'));
    if (row.userId) { q.set('userId', row.userId); q.set('userName', row.userName || ''); }
    if (tab === 'ip-traffic' && row.ip) q.set('ip', row.ip);
    this.props.history.push(`/${this.props.lang}/usage/${tab}?${q.toString()}`);
  }

  ruleLabel(rule) { return this.tr(`usage.alerts.rule.${rule}`, rule); }
  severityLabel(s) { return this.tr(`usage.alerts.severity.${s}`, s); }
  statusLabel(s) { return this.tr(`usage.alerts.status.${s}`, s); }

  // Coluna "Quem". Rajada de login por IP não tem usuário nem e-mail próprio: mostra os e-mails tentados naquele IP (evidência).
  whoText(row) {
    if (row.userName) return `${row.userName} (${row.userEmail || ''})`;
    if (row.userEmail) return row.userEmail;
    const e = row.evidence || {};
    if (row.rule === 'login_failures' && e.topEmailsText) return e.topEmailsText;
    return '—';
  }

  // Evidência por extenso: só os campos que a regra grava; o resto fica no JSON cru do diálogo.
  evidenceSummary(row) {
    const e = row.evidence || {};
    if (row.rule === 'new_country_login') return `${this.tr('usage.alerts.ev.country', 'país')} ${e.country || '?'}${e.city ? ` (${e.city})` : ''} · ${this.tr('usage.alerts.ev.base', 'base')} ${e.homeCountry || 'BR'} · ${this.tr('usage.alerts.ev.previous', 'antes')}: ${e.previousCountries || '—'}${e.asnOrg ? ` · ${e.asnOrg}` : ''}`;
    if (row.rule === 'hosting_or_tor_ip') return `${this.tr(`usage.ipt.kind.${e.kind || 'hosting'}`, e.kind || 'hosting')}${e.asnOrg ? ` · ${e.asnOrg}` : ''}${e.country ? ` · ${e.country}` : ''}${e.city ? ` (${e.city})` : ''}`;
    if (row.rule === 'login_failures') {
      let text = `${fmtInt(e.failures || 0)} ${this.tr('usage.alerts.ev.failures', 'falhas na última hora')} (${e.scope === 'ip' ? this.tr('usage.alerts.ev.byIp', 'por IP') : this.tr('usage.alerts.ev.byEmail', 'por e-mail')}; ${this.tr('usage.alerts.ev.threshold', 'limiar')} ${e.threshold || '?'})`;
      // Rajada por IP (evidência nova): senha errada × token inválido e os e-mails tentados. Alerta antigo não tem os campos e fica como era.
      if (e.scope === 'ip' && (e.passwordFailures != null || e.tokenInvalid != null))
        text += ` · ${this.tr('usage.alerts.ev.passwordFailures', 'senha errada')}: ${fmtInt(e.passwordFailures || 0)} · ${this.tr('usage.alerts.ev.tokenInvalid', 'token inválido')}: ${fmtInt(e.tokenInvalid || 0)}`;
      if (e.scope === 'ip' && e.topEmailsText) text += ` · ${this.tr('usage.alerts.ev.emails', 'e-mails tentados')}: ${e.topEmailsText}`;
      return text;
    }
    // 2b
    if (row.rule === 'denial_burst') return `${fmtInt(e.denials || 0)} ${this.tr('usage.alerts.ev.denials', 'recusas na última hora')} (${e.scope === 'ip' ? this.tr('usage.alerts.ev.byIp', 'por IP') : this.tr('usage.alerts.ev.byUser', 'por usuário')}; ${e.kinds || ''}; ${this.tr('usage.alerts.ev.threshold', 'limiar')} ${e.threshold || '?'})`;
    if (row.rule === 'export_burst') {
      if (e.scope === 'off_hours') return `${fmtInt(e.exports || 0)} ${this.tr('usage.alerts.ev.exportsOffHours', 'exportação(ões) de madrugada (0h–5h)')}, ${fmtInt(e.rows || 0)} ${this.tr('usage.alerts.ev.rows', 'linhas')} · ${this.tr('usage.alerts.ev.neverOffHours', 'nunca exportou nesse horário nos últimos 30 dias')}`;
      return `${fmtInt(e.exports || 0)} ${this.tr('usage.alerts.ev.exports', 'exportações na última hora')}, ${fmtInt(e.rows || 0)} ${this.tr('usage.alerts.ev.rows', 'linhas')} (${this.tr('usage.alerts.ev.threshold', 'limiar')} ${e.threshold || '?'})`;
    }
    if (row.rule === 'volume_anomaly') return `${fmtInt(e.rowsToday || 0)} ${this.tr('usage.alerts.ev.rowsToday', 'linhas devolvidas hoje')} · ${this.tr('usage.alerts.ev.usual', 'normal do usuário')} ${fmtInt(e.medianDaily || 0)}/${this.tr('usage.alerts.ev.day', 'dia')} (${fmtInt(e.historyDays || 0)} ${this.tr('usage.alerts.ev.days', 'dias')}) · ${this.tr('usage.alerts.ev.threshold', 'limiar')} ${fmtInt(e.threshold || 0)} × ${e.multiplier || '?'}`;
    return '';
  }

  renderKpis() {
    const { summary } = this.state;
    const { intl } = this.props;
    if (!summary) return null;
    return (
      <Grid container spacing={2} style={{ marginBottom: 8 }}>
        <Grid item xs={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.alerts.kpi.critical', 'Críticos abertos')} value={summary.openCritical} color={summary.openCritical ? '#c62828' : '#607D8B'} active={this.state.status === 'open' && this.state.severity === 'critical'} onClick={() => this.setChip({ status: 'open', severity: this.state.severity === 'critical' ? '' : 'critical' })} /></Grid>
        <Grid item xs={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.alerts.kpi.warning', 'Avisos abertos')} value={summary.openWarning} color={summary.openWarning ? '#ef6c00' : '#607D8B'} active={this.state.status === 'open' && this.state.severity === 'warning'} onClick={() => this.setChip({ status: 'open', severity: this.state.severity === 'warning' ? '' : 'warning' })} /></Grid>
        <Grid item xs={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.alerts.kpi.acknowledged', 'Reconhecidos')} value={summary.acknowledged} color="#1565c0" active={this.state.status === 'acknowledged'} onClick={() => this.setChip({ status: this.state.status === 'acknowledged' ? 'open' : 'acknowledged', severity: '' })} /></Grid>
        <Grid item xs={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.alerts.kpi.last7', 'Últimos 7 dias')} value={summary.last7Days} color="#3F51B4" subtitle={summary.byDay.length ? summary.byDay.map((d) => `${moment(d.day).format('DD/MM')}: ${d.count}`).join(' · ') : ''} /></Grid>
      </Grid>
    );
  }

  renderDialog() {
    const { dialog, saving } = this.state;
    if (!dialog) return null;
    const { row, action } = dialog;
    const resolve = action === 'resolved';
    return (
      <Dialog open onClose={() => !saving && this.setState({ dialog: null })} PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3, minWidth: 420 } }}>
        <DialogTitle>{resolve ? this.tr('usage.alerts.dialog.resolve', 'Resolver alerta') : this.tr('usage.alerts.dialog.ack', 'Reconhecer alerta')}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px 20px' }}>
            <Typography variant="body2" style={{ marginBottom: 8 }}><b>{this.ruleLabel(row.rule)}</b> · {row.userName || row.userEmail || row.ip} · {fmtDateTime(row.firstAt)}</Typography>
            <Typography variant="caption" style={{ color: '#666', display: 'block', marginBottom: 12 }}>{this.evidenceSummary(row)}</Typography>
            <TextField fullWidth multiline minRows={3} variant="outlined" label={this.tr('usage.alerts.dialog.note', 'Observação (opcional)')} value={dialog.note} inputProps={{ maxLength: 500 }} InputLabelProps={{ shrink: true }}
              onChange={(e) => this.setState({ dialog: { ...dialog, note: e.target.value } })} />
            <Typography variant="caption" style={{ color: '#888', display: 'block', marginTop: 8 }}>
              {resolve ? this.tr('usage.alerts.dialog.resolve.help', 'Resolvido é final: use quando a situação foi tratada (ou é legítima).') : this.tr('usage.alerts.dialog.ack.help', 'Reconhecido = alguém viu e está cuidando; o alerta sai da lista de abertos.')}
            </Typography>
            {row.evidence && <pre style={{ marginTop: 12, padding: 10, background: '#1a1a2e', color: '#e0e0e0', borderRadius: 8, fontSize: 11, overflowX: 'auto' }}>{JSON.stringify(row.evidence, null, 2)}</pre>}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.setState({ dialog: null })} disabled={saving}>{this.tr('usage.alerts.dialog.cancel', 'Cancelar')}</Button>
          <Button variant="contained" color={resolve ? 'success' : 'primary'} onClick={this.confirmStatus} disabled={saving} startIcon={saving ? <CircularProgress size={14} color="inherit" /> : resolve ? <DoneAllIcon /> : <CheckIcon />}>
            {resolve ? this.tr('usage.alerts.action.resolve', 'Resolver') : this.tr('usage.alerts.action.ack', 'Reconhecer')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { intl } = this.props;
    const { filter, loading, loaded, rows, count, page, status, severity, rule, snack } = this.state;
    const statusOptions = ['open', 'acknowledged', 'resolved'].map((s) => ({ id: s, label: this.statusLabel(s), color: STATUS_COLORS[s] }));
    const severityOptions = ['critical', 'warning', 'info'].map((s) => ({ id: s, label: this.severityLabel(s), color: SEVERITY_COLORS[s] }));
    const ruleOptions = RULES.map((r) => ({ id: r, label: this.ruleLabel(r) }));
    return (
      <Box>
        <UsagePeriodFilter intl={intl} value={filter} onChange={this.onFilter} showGroup={false} linkParams={{ status, severity, rule }} onApply={() => this.load(1)} applying={loading} />
        <Box style={{ marginTop: 8 }}>
          <UsageChipFilter label={this.tr('usage.alerts.col.status', 'Status')} allLabel={this.tr('usage.ua.any', 'Todos')} value={status} onChange={(v) => this.setChip({ status: v })} options={statusOptions} />
          <UsageChipFilter label={this.tr('usage.alerts.col.severity', 'Severidade')} allLabel={this.tr('usage.ua.any', 'Todas')} value={severity} onChange={(v) => this.setChip({ severity: v })} options={severityOptions} />
          <UsageChipFilter label={this.tr('usage.alerts.col.rule', 'Regra')} allLabel={this.tr('usage.ua.any', 'Todas')} value={rule} onChange={(v) => this.setChip({ rule: v })} options={ruleOptions} />
        </Box>
        {loading && !loaded && <Box display="flex" justifyContent="center" style={{ padding: 40 }}><CircularProgress color="secondary" /></Box>}
        {loaded && (
          <Box style={{ marginTop: 12 }}>
            {this.renderKpis()}
            {rows.length === 0 ? (
              <UsageEmpty intl={intl} icon={<NotificationsActiveIcon style={{ fontSize: 56 }} />} title={this.tr('usage.alerts.empty.title', 'Nenhum alerta neste recorte')} description={this.tr('usage.alerts.empty.description', 'O serviço de fundo avalia as regras a cada 5 minutos. Mude o status ou o período para ver outros alertas.')} />
            ) : (
              <Card variant="outlined" style={{ borderRadius: 12 }}>
                <CardContent style={{ padding: 0 }}>
                  <Box style={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell>{this.tr('usage.alerts.col.when', 'Quando')}</TableCell>
                        <TableCell>{this.tr('usage.alerts.col.severity', 'Severidade')}</TableCell>
                        <TableCell>{this.tr('usage.alerts.col.rule', 'Regra')}</TableCell>
                        <TableCell>{this.tr('usage.alerts.col.who', 'Quem')}</TableCell>
                        <TableCell>IP</TableCell>
                        <TableCell>{this.tr('usage.alerts.col.details', 'Detalhes')}</TableCell>
                        <TableCell>{this.tr('usage.alerts.col.status', 'Status')}</TableCell>
                        <TableCell className={STICKY_CELL_CLASS}>{this.tr('usage.alerts.col.actions', 'Ações')}</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {rows.map((r) => (
                          <TableRow key={r.id} hover>
                            <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.firstAt)}{r.count > 1 && <Tooltip title={`${this.tr('usage.alerts.repeated', 'repetido')} · ${this.tr('usage.alerts.lastAt', 'último')}: ${fmtDateTime(r.lastAt)}`}><span style={{ marginLeft: 6, color: '#888' }}>×{r.count}</span></Tooltip>}</TableCell>
                            <TableCell><Chip size="small" label={this.severityLabel(r.severity)} style={{ height: 20, fontSize: 11, backgroundColor: SEVERITY_COLORS[r.severity] || '#999', color: '#fff', fontWeight: 600 }} /></TableCell>
                            <TableCell style={{ whiteSpace: 'nowrap' }}>{this.ruleLabel(r.rule)}</TableCell>
                            <TableCell><UsageEllipsisCell text={this.whoText(r)} maxWidth={220} /></TableCell>
                            <TableCell style={{ whiteSpace: 'nowrap' }}>
                              {r.ip || '—'}
                              {(r.ipCountry || r.ipKind) && <span style={{ marginLeft: 6, fontSize: 11, color: r.ipKind === 'hosting' || r.ipKind === 'tor' ? '#c62828' : '#888' }}>{r.ipCountry || '?'}{r.ipCity ? ` · ${r.ipCity}` : ''}{r.ipAsnOrg ? ` · ${r.ipAsnOrg}` : ''}</span>}
                            </TableCell>
                            <TableCell><UsageEllipsisCell text={this.evidenceSummary(r)} maxWidth={320} /></TableCell>
                            <TableCell style={{ whiteSpace: 'nowrap' }}>
                              <Chip size="small" label={this.statusLabel(r.status)} variant="outlined" style={{ height: 20, fontSize: 11, borderColor: STATUS_COLORS[r.status], color: STATUS_COLORS[r.status], fontWeight: 600 }} />
                              {r.acknowledgedByName && <Tooltip title={`${r.acknowledgedByName} · ${fmtDateTime(r.acknowledgedAt)}${r.note ? ` · ${r.note}` : ''}`}><span style={{ marginLeft: 6, fontSize: 11, color: '#888' }}>{r.acknowledgedByName}</span></Tooltip>}
                            </TableCell>
                            <TableCell className={STICKY_CELL_CLASS} style={{ whiteSpace: 'nowrap' }}>
                              {r.status === 'open' && <Button size="small" startIcon={<CheckIcon />} onClick={() => this.openDialog(r, 'acknowledged')}>{this.tr('usage.alerts.action.ack', 'Reconhecer')}</Button>}
                              {r.status !== 'resolved' && <Button size="small" color="success" startIcon={<DoneAllIcon />} onClick={() => this.openDialog(r, 'resolved')}>{this.tr('usage.alerts.action.resolve', 'Resolver')}</Button>}
                              {r.userId && <Tooltip title={this.tr('usage.alerts.action.timeline', 'Linha do tempo do usuário')}><Button size="small" onClick={() => this.investigate(r, 'user-activity')}><TimelineIcon fontSize="small" /></Button></Tooltip>}
                              {r.userId && <Tooltip title={this.tr('usage.alerts.action.sessions', 'Logins e sessões')}><Button size="small" onClick={() => this.investigate(r, 'sessions')}><VpnKeyIcon fontSize="small" /></Button></Tooltip>}
                              {r.ip && <Tooltip title={this.tr('usage.alerts.action.ip', 'Tráfego por IP')}><Button size="small" onClick={() => this.investigate(r, 'ip-traffic')}><LanIcon fontSize="small" /></Button></Tooltip>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                  {count > PAGE_SIZE && <Box style={{ padding: 8 }}><Pagination count={Math.ceil(count / PAGE_SIZE)} page={page} onChange={this.onPage} /></Box>}
                </CardContent>
              </Card>
            )}
            <Alert severity="info" style={{ marginTop: 12 }}>{this.tr('usage.alerts.hint', 'Cada alerta chega por e-mail em resumo aos administradores globais e aos endereços fixos configurados. Regras e limiares: Configuração → Alertas de segurança.')}</Alert>
            <UsageGeoAttribution intl={intl} />
          </Box>
        )}
        {this.renderDialog()}
        <CustomizedSnackbars variant={snack.variant} message={snack.message} isOpen={snack.open} toClose={this.closeSnack} />
      </Box>
    );
  }
}
